import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { FiEdit2, FiTrash2 } from "react-icons/fi";

import GenericHeader from "../../../Componentes/GenericHeader";
import SortableTable from "../../../Componentes/Utils/SortableTable";
import FilterContainer from "../../../Componentes/Utils/FilterContainer";
import Filters from "../../../Componentes/Utils/Filters";
import { formatCPF } from "../../../Funcoes/FormatCPF";
import useApi from "../../../hooks/useApi";
import { useModal } from "../../../contexts/ModalContext";
import { AuthContext } from "../../../Auth/AuthProvider";
import { VerifyPermission } from "../../../Funcoes/VerifyPermission";

import ModalEditarTeste from "./ModalEditarTeste";
import ModalDeleteTeste from "./ModalDeleteTeste";
import ModalViewTeste from "./ModalViewTeste";
import { formatarDataHora, formatTipoTeste } from "./utils";

const PAGE_SIZE = 100;
const SCROLL_THRESHOLD = 40;
const PERMISSAO_BASE = { modulo: "ssma", submodulo: "atendimento-amb" };

const PageContent = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    box-sizing: border-box;
    overflow: hidden;
`;

const TableFlexFill = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
`;

const AcoesCelula = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
`;

const BotaoAcao = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    color: ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
    background-color: ${(p) =>
        p.$danger ? "rgba(185, 28, 28, 0.12)" : "rgba(29, 78, 216, 0.10)"};
    border: 1px solid transparent;

    &:hover {
        background-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.22)" : "rgba(29, 78, 216, 0.20)"};
        border-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.5)" : "rgba(29, 78, 216, 0.45)"};
    }
`;

function startOfDayMs(dateStr) {
    if (!dateStr) return null;
    return new Date(`${dateStr}T00:00:00`).getTime();
}
function endOfDayMs(dateStr) {
    if (!dateStr) return null;
    return new Date(`${dateStr}T23:59:59.999`).getTime();
}

function dataHoraToDayMs(value) {
    if (!value) return null;
    const datePart = String(value).slice(0, 10);
    return new Date(`${datePart}T00:00:00`).getTime();
}

function aplicarFiltros(lista, filtros) {
    const nome = filtros.nome.trim().toUpperCase();
    const cpf = filtros.cpf.replace(/\D/g, "");
    const tDe = startOfDayMs(filtros.dataDe);
    const tAte = endOfDayMs(filtros.dataAte);

    return lista.filter((row) => {
        const rNome = String(row.nome ?? "").toUpperCase();
        const rCpf = String(row.cpf ?? "");
        if (nome && !rNome.includes(nome)) return false;
        if (cpf && !rCpf.includes(cpf)) return false;
        const t = dataHoraToDayMs(row.data_hora);
        if (tDe != null && (t == null || t < tDe)) return false;
        if (tAte != null && (t == null || t > tAte)) return false;
        return true;
    });
}

const TestesAlcoolDrogasContent = ({ refreshKey = 0 }) => {
    const api = useApi();
    const { user } = useContext(AuthContext);
    const { openModal, closeModal, isModalOpen } = useModal();
    const tableRef = useRef(null);

    const podeLancarEditar = useMemo(
        () => VerifyPermission(user, { ...PERMISSAO_BASE, funcao: "testes-alcool-drogas-lancar-editar" }),
        [user]
    );
    const podeApagar = useMemo(
        () => VerifyPermission(user, { ...PERMISSAO_BASE, funcao: "testes-alcool-drogas-apagar" }),
        [user]
    );

    const [loading, setLoading] = useState(true);
    const [testes, setTestes] = useState([]);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    const [nome, setNome] = useState("");
    const [cpf, setCpf] = useState("");
    const [dataDe, setDataDe] = useState("");
    const [dataAte, setDataAte] = useState("");

    const [testeParaVisualizar, setTesteParaVisualizar] = useState(null);
    const [testeParaEditar, setTesteParaEditar] = useState(null);
    const [testeParaDelete, setTesteParaDelete] = useState(null);

    const filtros = useMemo(
        () => ({ nome, cpf, dataDe, dataAte }),
        [nome, cpf, dataDe, dataAte]
    );

    const filtered = useMemo(
        () => aplicarFiltros(testes, filtros),
        [testes, filtros]
    );

    const initialFetch = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/Painel/API/AtendimentoAmb/TestesAlcoolDrogas/List/index.php");
            setTestes(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Erro ao listar testes:", err);
            setTestes([]);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        initialFetch();
    }, [initialFetch, refreshKey]);

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [filtros]);

    const loadMoreRows = useCallback(() => {
        const total = filtered.length;
        setVisibleCount((prev) => {
            if (prev >= total) return prev;
            return Math.min(prev + PAGE_SIZE, total);
        });
    }, [filtered.length]);

    const abrirVisualizacao = (row) => {
        if (!row?.id) return;
        setTesteParaVisualizar(row.id);
        openModal("testeView");
    };

    const fecharVisualizacao = () => {
        setTesteParaVisualizar(null);
        closeModal();
    };

    const abrirEdicao = (row) => {
        if (!podeLancarEditar) return;
        closeModal();
        setTesteParaEditar(row);
        openModal("testeEdit");
    };
    const abrirDelete = (row) => {
        if (!podeApagar) return;
        setTesteParaDelete(row);
        openModal("testeDelete");
    };

    const fecharEdicao = () => { setTesteParaEditar(null); closeModal(); };
    const fecharDelete = () => { setTesteParaDelete(null); closeModal(); };

    const columns = useMemo(
        () => [
            { id: "id", header: "ID", accessor: "id", sortable: true, width: "70px", align: "center" },
            {
                id: "cpf",
                header: "CPF",
                accessor: "cpf",
                sortable: true,
                width: "140px",
                render: (v) => (v ? formatCPF(String(v)) : "-"),
            },
            { id: "nome", header: "NOME", accessor: "nome", sortable: true },
            { id: "funcao", header: "FUNÇÃO", accessor: "funcao", sortable: true },
            { id: "centro_custo", header: "CENTRO CUSTO", accessor: "centro_custo", sortable: true, width: "130px" },
            { id: "contrato", header: "CONTRATO", accessor: "contrato", sortable: true, width: "120px" },
            { id: "lider", header: "LÍDER", accessor: "lider", sortable: true, width: "120px" },
            {
                id: "tipo_teste",
                header: "TIPO",
                accessor: "tipo_teste",
                sortable: true,
                width: "120px",
                render: (v) => formatTipoTeste(v),
            },
            {
                id: "data_hora",
                header: "DATA/HORA",
                accessor: "data_hora",
                sortable: true,
                width: "150px",
                render: (v) => formatarDataHora(v),
                sortValue: (row) => row.data_hora ?? "",
            },
            ...(podeLancarEditar || podeApagar
                ? [{
                    id: "acoes",
                    header: "AÇÕES",
                    sortable: false,
                    width: "90px",
                    align: "center",
                    render: (_, row) => (
                        <AcoesCelula>
                            {podeLancarEditar && (
                                <BotaoAcao
                                    title="Editar"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        abrirEdicao(row);
                                    }}
                                >
                                    <FiEdit2 size={14} />
                                </BotaoAcao>
                            )}
                            {podeApagar && (
                                <BotaoAcao
                                    $danger
                                    title="Remover"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        abrirDelete(row);
                                    }}
                                >
                                    <FiTrash2 size={14} />
                                </BotaoAcao>
                            )}
                        </AcoesCelula>
                    ),
                }]
                : []),
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [podeLancarEditar, podeApagar]
    );

    return (
        <>
            <PageContent>
                <GenericHeader responsive>
                    <Filters columns={4}>
                        <FilterContainer
                            type="text"
                            placeholder="NOME COLABORADOR"
                            valor={nome}
                            changeFunc={setNome}
                        />
                        <FilterContainer
                            type="text"
                            placeholder="CPF"
                            valor={cpf}
                            changeFunc={setCpf}
                            maxLength={14}
                        />
                        <FilterContainer
                            type="date"
                            placeholder="DATA — A PARTIR DE"
                            valor={dataDe}
                            changeFunc={setDataDe}
                        />
                        <FilterContainer
                            type="date"
                            placeholder="DATA — ATÉ"
                            valor={dataAte}
                            changeFunc={setDataAte}
                        />
                    </Filters>
                </GenericHeader>

                <TableFlexFill>
                    <SortableTable
                        ref={tableRef}
                        columns={columns}
                        data={filtered}
                        limit={visibleCount}
                        onScrollReachEnd={loadMoreRows}
                        scrollReachThreshold={SCROLL_THRESHOLD}
                        isLoading={loading}
                        loadingText="Carregando testes..."
                        emptyText="Nenhum teste encontrado."
                        initialSort={{ key: "data_hora", direction: "desc" }}
                        getRowId={(row) => row.id}
                        onRowClick={abrirVisualizacao}
                    />
                </TableFlexFill>
            </PageContent>

            {isModalOpen("testeView") && testeParaVisualizar != null && (
                <ModalViewTeste
                    testeId={testeParaVisualizar}
                    fechar={fecharVisualizacao}
                />
            )}

            {podeLancarEditar && isModalOpen("testeEdit") && testeParaEditar != null && (
                <ModalEditarTeste
                    teste={testeParaEditar}
                    fechar={fecharEdicao}
                    onSaved={() => { fecharEdicao(); initialFetch(); }}
                />
            )}

            {podeApagar && isModalOpen("testeDelete") && testeParaDelete != null && (
                <ModalDeleteTeste
                    teste={testeParaDelete}
                    fecharModal={fecharDelete}
                    onDeleted={initialFetch}
                />
            )}
        </>
    );
};

export default TestesAlcoolDrogasContent;
